// Read a media file's intrinsic dimensions/duration in the browser before upload, so the
// server can store them (it can't decode the file in the Workers runtime). Best-effort:
// resolves with whatever it could read (nulls otherwise).

import { readExifDateTaken } from './exif';

export interface MediaMeta {
  kind: 'photo' | 'video' | 'other';
  width: number | null;
  height: number | null;
  duration: number | null; // seconds (video only)
  takenAt: string | null;  // 'YYYY-MM-DD HH:MM:SS' from EXIF DateTimeOriginal (photos only)
  thumbnailBlob: Blob | null; // small JPEG generated in the browser (photos only)
}

const PHOTO = /^image\/(jpeg|png|webp|gif)$/;
const VIDEO = /^video\/(mp4|quicktime|webm)$/;

export function isSupportedMedia(file: File): boolean {
  return PHOTO.test(file.type) || VIDEO.test(file.type);
}

/** True if an R2 key/URL points at an image (used to decide <img> vs <video> rendering —
 *  a video with a generated poster has an image thumbnail_url, an unprocessed one doesn't). */
export function isImageKey(key: string | null | undefined): boolean {
  return !!key && /\.(jpe?g|png|webp|gif)$/i.test(key);
}

/** Build the query string the gallery item-upload endpoints expect from read metadata. */
export function mediaUploadQuery(file: File, meta: MediaMeta): string {
  const qs = new URLSearchParams({ filename: file.name });
  if (meta.width) qs.set('w', String(meta.width));
  if (meta.height) qs.set('h', String(meta.height));
  if (meta.duration != null) qs.set('duration', String(meta.duration));
  if (meta.takenAt) qs.set('taken', meta.takenAt);
  return qs.toString();
}

export async function readMediaMeta(file: File): Promise<MediaMeta> {
  if (PHOTO.test(file.type)) return readImageMeta(file);
  if (VIDEO.test(file.type)) return readVideoMeta(file);
  return { kind: 'other', width: null, height: null, duration: null, takenAt: null, thumbnailBlob: null };
}

async function readImageMeta(file: File): Promise<MediaMeta> {
  // One decode produces both the dimensions and a small JPEG thumbnail; EXIF is read in
  // parallel. The thumbnail is what makes tiles render instantly instead of waiting for
  // the multi-MB original to download.
  const [thumb, takenAt] = await Promise.all([makeImageThumbnail(file), readExifDateTaken(file)]);
  if (thumb) {
    return { kind: 'photo', width: thumb.width, height: thumb.height, duration: null, takenAt, thumbnailBlob: thumb.blob };
  }
  const dims = await readImageDims(file); // thumbnail generation unavailable — still record dimensions
  return { ...dims, takenAt, thumbnailBlob: null };
}

/** Decode an image and produce a downscaled JPEG (longest side ≤ maxPx) for use as a
 *  thumbnail, plus the original pixel dimensions. Returns null if decoding isn't possible. */
async function makeImageThumbnail(file: File, maxPx = 480): Promise<{ blob: Blob; width: number; height: number } | null> {
  if (typeof createImageBitmap !== 'function') return null;
  let bitmap: ImageBitmap;
  try {
    // `from-image` applies EXIF orientation so the thumbnail isn't sideways.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch { return null; }
  try {
    const width = bitmap.width, height = bitmap.height;
    const scale = Math.min(1, maxPx / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale)), h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.82));
    return blob ? { blob, width, height } : null;
  } finally {
    bitmap.close();
  }
}

function readImageDims(file: File): Promise<{ kind: 'photo'; width: number | null; height: number | null; duration: null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ kind: 'photo', width: img.naturalWidth || null, height: img.naturalHeight || null, duration: null });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve({ kind: 'photo', width: null, height: null, duration: null }); };
    img.src = url;
  });
}

async function readVideoMeta(file: File): Promise<MediaMeta> {
  // Capture a poster frame so tiles/covers render an <img> instead of loading the whole
  // <video> (which can be slow to show a first frame over the network). Falls back to a
  // metadata-only read if a frame can't be grabbed (unsupported codec, etc.).
  const poster = await makeVideoThumbnail(file);
  if (poster) {
    return { kind: 'video', width: poster.width || null, height: poster.height || null, duration: poster.duration, takenAt: null, thumbnailBlob: poster.blob };
  }
  return readVideoDims(file);
}

/** Seek a video to an early frame and export it as a small JPEG poster. */
function makeVideoThumbnail(file: File, maxPx = 480): Promise<{ blob: Blob; width: number; height: number; duration: number | null } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true; (video as any).playsInline = true;
    video.preload = 'auto';
    let settled = false;
    const finish = (v: { blob: Blob; width: number; height: number; duration: number | null } | null) => {
      if (settled) return; settled = true;
      clearTimeout(timer); URL.revokeObjectURL(url); resolve(v);
    };
    const timer = setTimeout(() => finish(null), 15000); // safety net for stuck decodes
    video.onloadedmetadata = () => {
      const d = video.duration;
      // A frame a touch into the clip avoids black intro frames; clamp to the clip length.
      video.currentTime = Number.isFinite(d) && d > 0 ? Math.min(1, d / 2) : 0;
    };
    video.onseeked = () => {
      try {
        const width = video.videoWidth, height = video.videoHeight;
        if (!width || !height) return finish(null);
        const scale = Math.min(1, maxPx / Math.max(width, height));
        const w = Math.max(1, Math.round(width * scale)), h = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return finish(null);
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => finish(blob ? { blob, width, height, duration: Number.isFinite(video.duration) ? Math.round(video.duration) : null } : null),
          'image/jpeg', 0.82,
        );
      } catch { finish(null); }
    };
    video.onerror = () => finish(null);
    video.src = url;
  });
}

function readVideoDims(file: File): Promise<MediaMeta> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        kind: 'video',
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        duration: Number.isFinite(video.duration) ? Math.round(video.duration) : null,
        takenAt: null,
        thumbnailBlob: null,
      });
    };
    video.onerror = () => { URL.revokeObjectURL(url); resolve({ kind: 'video', width: null, height: null, duration: null, takenAt: null, thumbnailBlob: null }); };
    video.src = url;
  });
}
